<template>
  <div :class="{ 'tabs--icon-tabs': iconTabs }">
    <div
      :class="[
        'tabs__header',
        iconTabs ? 'tabs__header--icon-tabs' : pills ? 'tabs__header--pills' : 'tabs__header--desktop',
      ]"
      role="tablist"
      aria-label="Tabs"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="slugify(tab.props?.title)"
        :key="tab.key as number"
        :uuid="index"
        role="tab"
        :aria-selected="index == selectedIndex"
        :aria-controls="`tabpanel-${index}`"
        :class="[
          { tab__selected: index == selectedIndex },
          iconTabs ? 'tab--icon-tabs' : '',
          { 'tab--pills paragraph-m': pills && !iconTabs },
          { 'heading--5': !pills && !iconTabs },
          'tab',
        ]"
        @click="(e) => selectTab(e, index)"
      >
        <img          v-if="iconTabs && resolveIcon(tab, index)"
          :src="resolveIcon(tab, index)"
          alt=""
          aria-hidden="true"
          class="tab__icon-img"
        />
        <span class="tab__icon-label">{{ tab.props?.title }}</span>
      </button>
    </div>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { slugify } from '@/utils/helpers'

const slots = useSlots()
const emit = defineEmits({
  tabChanged(index: number) {
    return index !== undefined || index !== null
  },
})

interface Props {
  pills?: boolean
  iconTabs?: boolean
}

const props = defineProps<Props>()

const tabs = computed(() => {
  return slots.default?.()[0]?.children as VNode[]
})

const selectedIndex = ref(0)

provide('selectedIndex', selectedIndex)

function resolveIcon(tab: VNode, index: number): string | undefined {  const isActive = index === selectedIndex.value
  const tabProps = tab.props as any
  const iconActive = tabProps?.iconActive || tabProps?.['icon-active']
  const icon = tabProps?.icon
  if (isActive && iconActive?.filename) return iconActive.filename
  return icon?.filename
}

const selectTab = (_event: Event, index: number) => {
  selectedIndex.value = index
  if (Array.isArray(tabs.value)) {
    tabs.value?.forEach((tab: any, index) => {
      tab.isActive = index === selectedIndex.value
    })
  }
  emit('tabChanged', index)
}
</script>

<style lang="scss">
.tabs--icon-tabs {
  background: var(--paper-200);
  border-radius: 16px;
  padding: 12px;
  padding-bottom: 40px;
}

.tabs--icon-tabs .tabs__header--icon-tabs {
  margin-bottom: 32px;
}

.tabs__header {
  display: flex;
  list-style: none;
  margin-bottom: var(--space56);

  &--mobile {
    display: flex;
    flex-direction: column;
    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &--desktop {
    display: none;
    @media (min-width: $breakpoint-lg) {
      display: flex;
    }
  }

  &--pills {
    display: flex !important;
    gap: var(--space16);
    flex-direction: row;
    flex-wrap: wrap;
  }

  &--icon-tabs {
    display: flex !important;
    gap: 12px;
    flex-direction: row;
    margin-bottom: 0;
    width: 100%;
  }
}

.tab {
  padding: 0 var(--space32) var(--space24) var(--space24);
  border-bottom: 2px solid var(--gray-400);
  display: inline-block;
  text-align: left;

  &--pills {
    @include shadow-grey-small;

    padding: var(--space12) var(--space24);
    border-radius: 30px;
    border: none !important;
    background-color: var(--white);
    font-weight: var(--font-weight-semibold);
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: var(--cobalt-200);
    }
  }

  &--icon-tabs {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px;
    flex: 1;
    justify-content: center;
    border-radius: 12px;
    border: none !important;
    background: transparent;
    color: var(--navy-600);
    cursor: pointer;
    transition:
      background 0.2s ease,
      box-shadow 0.2s ease;

    .tab__icon-img {
      width: 20px;
      height: 20px;
      object-fit: contain;
      flex-shrink: 0;
    }

    .tab__icon-label {
      font-size: var(--font-size-paragraph-s);
      font-weight: var(--font-weight-bold);
      line-height: var(--line-height-paragraph-s);
    }

    &.tab__selected {
      background: var(--white);
      color: var(--navy-800);
      border-bottom: none;
      @include shadow-small-soft;
    }
  }
}

.tab:first-of-type:not(.tab--pills):not(.tab--icon-tabs) {
  padding-left: 0;
}

.tab__selected {
  color: var(--cobalt-600);
  border-bottom: 2px solid var(--cobalt-600);

  &.tab--pills {
    background-color: var(--cobalt-200);
    color: var(--color-text);
  }
}
</style>
